import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useVoiceFixture } from "@/hooks/useVoiceFixture";
import { useNextFixture } from "@/hooks/useNextFixture";
import { FixtureBanner } from "@/components/FixtureBanner";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { TeamSelect } from "@/components/TeamSelect";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { EmailReportButton } from "@/components/EmailReportButton";
import { useAva } from "@/context/AvaContext";
import { Swords, Sun, CloudRain, Wind, Snowflake, Droplets, Loader2, ArrowLeftRight, ShieldAlert, Zap, Volume2, Minus, Plus, Activity, Flame, Play, Pause, Gauge, Target, TrendingUp, ClipboardList, Grid3x3, Radio, Globe } from "lucide-react";
import { toast } from "sonner";

const sel = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50 max-w-[220px]";
const WEATHER = [{ v: "dry", l: "Dry", i: Sun }, { v: "rain", l: "Rain / wet", i: CloudRain }, { v: "wind", l: "Windy", i: Wind }, { v: "heat", l: "Hot / humid", i: Droplets }, { v: "cold", l: "Cold", i: Snowflake }];
const eColor = (v) => (v >= 75 ? "#ccff00" : v >= 55 ? "#ffb800" : v >= 40 ? "#ff7300" : "#ff3b30");

const Stepper = ({ label, value, set, testid }) => (
  <div className="flex flex-col items-center gap-1">
    <span className="font-tac text-[9px] tracking-widest text-white/40">{label}</span>
    <div className="flex items-center gap-2">
      <button onClick={() => set(Math.max(0, value - 1))} aria-label={`${label} minus one`} className="rounded-lg border border-white/10 p-1.5 text-white/60 hover:text-white" data-testid={`${testid}-minus`}><Minus size={14} /></button>
      <span className="w-8 text-center font-head text-3xl font-black" data-testid={testid}>{value}</span>
      <button onClick={() => set(value + 1)} aria-label={`${label} plus one`} className="rounded-lg border border-white/10 p-1.5 text-white/60 hover:text-white" data-testid={`${testid}-plus`}><Plus size={14} /></button>
    </div>
  </div>
);

const Card = ({ icon: Icon, title, children, testid, accent = "#00e5ff" }) => (
  <div className="card-tac p-5" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2"><Icon size={16} style={{ color: accent }} /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">{title}</h3></div>
    {children}
  </div>
);

// Swinging momentum bar (-100 their favour … +100 our favour) with live xG
const MomentumBar = ({ mom, team, opponent }) => {
  const v = Math.max(-100, Math.min(100, mom.value));
  const ourSide = v >= 0;
  const col = v >= 18 ? "#ccff00" : v <= -18 ? "#ff3b30" : "#ffb800";
  return (
    <div className="card-tac p-5" data-testid="live-momentum" data-octon-section="momentum bar swing live xg" data-octon-label="Momentum">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Activity size={16} style={{ color: col }} /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">LIVE MOMENTUM</h3></div>
        <span className="font-tac text-[10px] tracking-widest" style={{ color: col }} data-testid="momentum-swing">SWINGING {mom.swing.toUpperCase()}</span>
      </div>
      <div className="mb-1 flex justify-between text-[11px]">
        <span className="truncate font-semibold" style={{ color: ourSide ? col : "rgba(255,255,255,0.5)" }}>{team}</span>
        <span className="truncate font-semibold" style={{ color: !ourSide ? col : "rgba(255,255,255,0.5)" }}>{opponent}</span>
      </div>
      {/* centre-anchored swing bar */}
      <div className="relative h-4 w-full overflow-hidden rounded-full bg-white/8">
        <div className="absolute left-1/2 top-0 h-full w-px bg-white/25" />
        <motion.div className="absolute top-0 h-full" data-testid="momentum-fill"
          initial={false}
          animate={ourSide ? { left: "50%", width: `${(v / 100) * 50}%` } : { right: "50%", width: `${(-v / 100) * 50}%`, left: "auto" }}
          transition={{ type: "spring", stiffness: 90, damping: 18 }}
          style={{ background: col, boxShadow: `0 0 12px ${col}`, borderRadius: 999 }} />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-left"><p className="font-head text-2xl font-black" style={{ color: ourSide ? col : "#fff" }}>{mom.our_xg}</p><p className="font-tac text-[8px] tracking-widest text-white/35">OUR xG</p></div>
        <div className="text-center"><p className="font-head text-xl font-black" style={{ color: col }}>{v > 0 ? "+" : ""}{v}</p><p className="font-tac text-[8px] tracking-widest text-white/35">MOMENTUM</p></div>
        <div className="text-right"><p className="font-head text-2xl font-black" style={{ color: !ourSide ? col : "#fff" }}>{mom.their_xg}</p><p className="font-tac text-[8px] tracking-widest text-white/35">THEIR xG</p></div>
      </div>
    </div>
  );
};

const decisionMeta = (d = "") => {
  if (d.startsWith("TACTICAL CHANGE")) return { color: "#ff3b30", label: "TACTICAL CHANGE REQUIRED" };
  if (d.startsWith("DEFENSIVE ADJUSTMENT")) return { color: "#ffb800", label: "DEFENSIVE ADJUSTMENT REQUIRED" };
  return { color: "#33d17a", label: "MAINTAIN SYSTEM" };
};

// Efficiency gauge — how a side is executing vs its chronological xG baseline (1.0 = on plan)
const EffMeter = ({ label, value, invert = false }) => {
  const good = invert ? value < 1.0 : value >= 1.0;
  const col = good ? "#33d17a" : value >= (invert ? 1.25 : 0.8) ? "#ffb800" : "#ff3b30";
  const pct = Math.max(4, Math.min(100, value * 50));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="text-white/55">{label}</span>
        <span className="font-mono font-bold" style={{ color: col }}>{value.toFixed(2)}×</span>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-white/8">
        <div className="absolute left-1/2 top-0 h-full w-px bg-white/30" />
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col, boxShadow: `0 0 10px ${col}` }} />
      </div>
    </div>
  );
};

// Live in-play coaching-matrix evaluation
const CoachingMatrix = ({ cm }) => {
  const dm = decisionMeta(cm.decision);
  const P = cm.projections;
  return (
    <div className="card-tac p-5" data-testid="live-coaching-matrix" data-octon-section="coaching matrix efficiency projections decision" data-octon-label="Coaching Matrix">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Gauge size={16} style={{ color: "#00e5ff" }} /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">COACHING MATRIX · LIVE EVALUATION</h3></div>
        <span className="font-tac text-[9px] tracking-widest text-white/35">{cm.minute}' · {cm.live_xg.coach_supplied ? "COACH xG FEED" : "OCTON xG MODEL"}</span>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-3">
          <EffMeter label="Our threat efficiency" value={cm.our_efficiency} />
          <EffMeter label="Opponent threat efficiency" value={cm.opp_efficiency} invert />
          <div className="grid grid-cols-2 gap-2 pt-1 text-[10px]">
            <div className="rounded-lg border border-white/8 bg-white/[0.02] p-2"><p className="text-white/35">LIVE xG</p><p className="font-mono text-white/80" data-testid="cm-live-xg">{cm.live_xg.us} <span className="text-white/30">/</span> {cm.live_xg.them}</p></div>
            <div className="rounded-lg border border-white/8 bg-white/[0.02] p-2"><p className="text-white/35">EXPECTED BY NOW</p><p className="font-mono text-white/80">{cm.expected_so_far.us} <span className="text-white/30">/</span> {cm.expected_so_far.them}</p></div>
          </div>
          <p className="text-[9px] text-white/35">Baseline (90') {cm.baseline_xg.us} / {cm.baseline_xg.them} · efficiency = live xG ÷ chronological expectation.</p>
        </div>
        <div className="flex flex-col justify-center">
          <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">PROJECTED OUTCOME FROM THIS MINUTE</p>
          <div className="flex h-4 w-full overflow-hidden rounded-full">
            <div style={{ width: `${P.win}%`, background: "#33d17a" }} title={`Win ${P.win}%`} data-testid="cm-proj-win" />
            <div style={{ width: `${P.draw}%`, background: "#8a8f98" }} title={`Draw ${P.draw}%`} data-testid="cm-proj-draw" />
            <div style={{ width: `${P.loss}%`, background: "#ff3b30" }} title={`Loss ${P.loss}%`} data-testid="cm-proj-loss" />
          </div>
          <div className="mt-2 flex justify-between font-mono text-[11px]">
            <span className="text-[#33d17a]">WIN {P.win}%</span>
            <span className="text-white/50">DRAW {P.draw}%</span>
            <span className="text-[#ff3b30]">LOSS {P.loss}%</span>
          </div>
        </div>
        <div className="flex flex-col justify-center rounded-2xl border p-4" style={{ borderColor: `${dm.color}55`, background: `${dm.color}0f` }} data-testid="cm-decision">
          <div className="mb-1.5 flex items-center gap-2"><ClipboardList size={14} style={{ color: dm.color }} /><span className="font-tac text-[9px] tracking-widest" style={{ color: dm.color }}>{dm.label}</span></div>
          <p className="text-[13px] font-semibold leading-snug text-white/90">{cm.decision.split(":").slice(1).join(":").trim() || cm.decision}</p>
        </div>
      </div>
    </div>
  );
};

// Pre-match formation optimizer (coaching-matrix blueprint)
const Blueprint = ({ bp }) => (
  <div className="card-tac p-5" data-testid="blueprint-result" data-octon-section="tactical blueprint formations win draw loss" data-octon-label="Tactical Blueprint">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2"><Grid3x3 size={16} style={{ color: "#ccff00" }} /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">PRE-MATCH TACTICAL BLUEPRINT</h3></div>
      <span className="font-tac text-[9px] tracking-widest text-white/35">{bp.team} vs {bp.opponent} · {bp.venue.toUpperCase()}</span>
    </div>
    {bp.optimal && (
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/[0.05] p-4" data-testid="blueprint-optimal">
        <div><p className="font-tac text-[8px] tracking-widest text-white/40">OPTIMAL SHAPE</p><p className="font-head text-3xl font-black text-[#ccff00]">{bp.optimal.formation}</p><p className="text-[10px] text-white/50">{bp.optimal.style}</p></div>
        <div className="flex gap-4">
          <div><p className="font-tac text-[8px] tracking-widest text-white/40">WIN</p><p className="font-head text-2xl font-black text-[#33d17a]">{bp.optimal.win_pct}%</p></div>
          <div><p className="font-tac text-[8px] tracking-widest text-white/40">OUR xG μ</p><p className="font-head text-2xl font-black text-white">{bp.optimal.our_mu}</p></div>
          <div><p className="font-tac text-[8px] tracking-widest text-white/40">CONCEDE ν</p><p className="font-head text-2xl font-black text-white/70">{bp.optimal.opp_nu}</p></div>
        </div>
        <div className="ml-auto max-w-[220px] text-right"><p className="font-tac text-[8px] tracking-widest text-white/40">KEY THREAT</p><p className="text-[12px] text-white/75">{bp.optimal.key_players.join(", ")}</p></div>
      </div>
    )}
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[12px]">
        <thead><tr className="font-tac text-[9px] tracking-widest text-white/35">
          <th className="pb-2 pr-3">FORMATION</th><th className="pb-2 pr-3">STYLE</th>
          <th className="pb-2 pr-3 text-center">WIN</th><th className="pb-2 pr-3 text-center">DRAW</th><th className="pb-2 pr-3 text-center">LOSS</th>
          <th className="pb-2 pr-3 text-center">xG μ</th><th className="pb-2 text-center">ν</th>
        </tr></thead>
        <tbody>{bp.matrix.map((r, i) => (
          <tr key={r.formation} className={`border-t border-white/6 ${i === 0 ? "bg-[#ccff00]/[0.04]" : ""}`} data-testid={`blueprint-row-${r.formation}`}>
            <td className="py-2 pr-3 font-head font-black text-white">{r.formation}</td>
            <td className="py-2 pr-3 text-white/45">{r.style}</td>
            <td className="py-2 pr-3 text-center font-mono font-bold text-[#33d17a]">{r.win_pct}%</td>
            <td className="py-2 pr-3 text-center font-mono text-white/45">{r.draw_pct}%</td>
            <td className="py-2 pr-3 text-center font-mono text-[#ff3b30]">{r.loss_pct}%</td>
            <td className="py-2 pr-3 text-center font-mono text-white/75">{r.our_mu}</td>
            <td className="py-2 text-center font-mono text-white/55">{r.opp_nu}</td>
          </tr>))}</tbody>
      </table>
    </div>
    <p className="mt-3 text-[10px] text-white/35">Opponent scouting — attack threat {bp.opponent_scouting.attack_threat}× · defensive vulnerability {bp.opponent_scouting.defense_weakness}×. {bp.model}</p>
  </div>
);

// Live Data Ingestion Engine panel — auto-streamed from API-Football + web cross-check
const StreamPanel = ({ s, off, on, setOff, setOn, onTrial, trialLoading, trialResult }) => {
  if (!s) return null;
  if (!s.live) return (
    <div className="card-tac p-5" data-testid="stream-panel" data-octon-section="live stream feed xg efficiency guidance web check" data-octon-label="Live Stream Panel">
      <div className="mb-2 flex items-center gap-2"><Radio size={16} className="text-white/40" /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">LIVE DATA STREAM</h3></div>
      <p className="text-[13px] text-white/55" data-testid="stream-waiting">{s.message}</p>
      <p className="mt-1 text-[10px] text-white/35">OCTON is watching API-Football for {s.team}'s kick-off. Baseline plan: xG {s.baseline_xg?.us} / {s.baseline_xg?.them} · {s.shape}.</p>
    </div>
  );
  const dm = decisionMeta(s.engine.guidance.startsWith("CRITICAL") ? "TACTICAL CHANGE" : "");
  return (
    <div className="card-tac p-5" data-testid="stream-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#ccff00] opacity-70" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ccff00]" /></span>
          <h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">LIVE DATA STREAM · {s.league}</h3>
        </div>
        <span className="font-tac text-[9px] tracking-widest text-white/35" data-testid="stream-clock">{s.team} {s.score.us}-{s.score.them} {s.opponent} · {s.minute}' · {s.status}</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div>
          <p className="font-tac text-[9px] tracking-widest text-white/40">LIVE xG {s.live_xg.available ? "· API FEED" : "· MODELLED"}</p>
          <p className="mt-1 font-head text-3xl font-black" data-testid="stream-xg"><span className="txt-cyan">{s.live_xg.us}</span><span className="mx-2 text-white/25">/</span><span className="text-white/70">{s.live_xg.them}</span></p>
          <p className="mt-1 text-[10px] text-white/35">Baseline (90') {s.baseline_xg.us} / {s.baseline_xg.them} · {s.shape} (att {s.formation_weights.attack} / def {s.formation_weights.defense})</p>
          <div className="mt-2 flex gap-3 text-[11px]"><span className="text-white/40">Efficiency</span><span className="font-mono" style={{ color: s.efficiency.us >= 1 ? "#33d17a" : "#ff3b30" }}>us {s.efficiency.us}×</span><span className="font-mono text-white/50">them {s.efficiency.them}×</span></div>
        </div>
        <div className="flex flex-col justify-center">
          <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">OUTLOOK FROM HERE</p>
          <div className="flex h-4 w-full overflow-hidden rounded-full">
            <div style={{ width: `${s.engine.win}%`, background: "#33d17a" }} data-testid="stream-win" />
            <div style={{ width: `${s.engine.draw}%`, background: "#8a8f98" }} data-testid="stream-draw" />
            <div style={{ width: `${s.engine.loss}%`, background: "#ff3b30" }} data-testid="stream-loss" />
          </div>
          <div className="mt-2 flex justify-between font-mono text-[11px]"><span className="text-[#33d17a]">W {s.engine.win}%</span><span className="text-white/50">D {s.engine.draw}%</span><span className="text-[#ff3b30]">L {s.engine.loss}%</span></div>
        </div>
        <div className="flex flex-col justify-center rounded-2xl border p-4" style={{ borderColor: `${dm.color}55`, background: `${dm.color}0f` }} data-testid="stream-guidance">
          <div className="mb-1.5 flex items-center gap-2"><Radio size={14} style={{ color: dm.color }} /><span className="font-tac text-[9px] tracking-widest" style={{ color: dm.color }}>{s.engine.underperforming ? "CRITICAL ACTION" : "ON TARGET"}</span></div>
          <p className="text-[13px] font-semibold leading-snug text-white/90">{s.engine.guidance.split(":").slice(1).join(":").trim() || s.engine.guidance}</p>
        </div>
      </div>
      {s.subs?.on_pitch?.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#33d17a]/20 bg-[#33d17a]/[0.03] p-3" data-testid="stream-sub-trial">
          <p className="mb-2 flex items-center gap-1.5 font-tac text-[9px] tracking-widest text-[#33d17a]"><ArrowLeftRight size={11} /> TRIAL A SUB — LIVE (feed keeps running)</p>
          <div className="flex flex-wrap items-end gap-2.5">
            <select data-testid="stream-sub-off" value={off} onChange={(e) => setOff(e.target.value)} className={selSm}>
              <option value="">Bring off…</option>
              {s.subs.on_pitch.map((p) => <option key={p.id} value={p.id}>{`${p.position} · ${p.name} (${p.readiness}%)`}</option>)}
            </select>
            <ArrowLeftRight size={14} className="mb-2.5 text-white/30" />
            <select data-testid="stream-sub-on" value={on} onChange={(e) => setOn(e.target.value)} className={selSm}>
              <option value="">Bring on…</option>
              {s.subs.bench.map((p) => <option key={p.id} value={p.id}>{`${p.position} · ${p.name} (${p.readiness}%)`}</option>)}
            </select>
            <button onClick={onTrial} disabled={trialLoading} data-testid="stream-sub-run"
              className="inline-flex items-center gap-2 rounded-full bg-[#33d17a] px-4 py-2 text-[13px] font-bold text-black transition hover:shadow-[0_0_24px_rgba(51,209,122,0.5)] disabled:opacity-50">
              {trialLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} Trial
            </button>
            {trialResult && (
              <div className="flex items-center gap-3" data-testid="stream-sub-result">
                <span className="font-mono text-[13px] text-white/55">{trialResult.before.win}%</span>
                <span className="text-white/30">→</span>
                <span className="font-head text-2xl font-black" style={{ color: trialResult.win_swing >= 0 ? "#33d17a" : "#ff3b30" }} data-testid="stream-sub-after">{trialResult.after.win}%</span>
                <span className="rounded-full px-2.5 py-0.5 font-mono text-[13px] font-bold" data-testid="stream-sub-swing"
                  style={{ background: trialResult.win_swing >= 0 ? "#33d17a22" : "#ff3b3022", color: trialResult.win_swing >= 0 ? "#33d17a" : "#ff3b30" }}>
                  {trialResult.win_swing > 0 ? "+" : ""}{trialResult.win_swing} pts
                </span>
                <span className="text-[11px] text-white/45">{trialResult.verdict}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {s.web_check && (
        <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.03] p-3" data-testid="stream-webcheck">
          <p className="mb-1 flex items-center gap-1.5 font-tac text-[9px] tracking-widest txt-cyan"><Target size={11} /> WEB CROSS-CHECK</p>
          <p className="text-[12px] leading-relaxed text-white/65">{s.web_check}</p>
        </div>
      )}
    </div>
  );
};

// Live Board — every fixture in play worldwide (Multi-Match Watch)
const LiveBoard = ({ board, onWatch, activeIds }) => (
  <div className="card-tac p-5" data-testid="live-board" data-octon-section="live board matches worldwide in play" data-octon-label="Live Board">
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2"><Globe size={16} className="txt-cyan" /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">LIVE BOARD · SCOUT THE WORLD</h3></div>
      <span className="font-tac text-[9px] tracking-widest text-white/35">{board?.count || 0} IN PLAY</span>
    </div>
    {!board?.matches?.length ? (
      <p className="text-[13px] text-white/50" data-testid="board-empty">No matches in play right now — check back at kick-off times.</p>
    ) : (
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 max-h-[420px] overflow-y-auto pr-1">
        {board.matches.map((m) => {
          const active = activeIds.has(m.home?.id) && activeIds.has(m.away?.id);
          return (
            <div key={m.fixture_id} data-testid={`board-match-${m.fixture_id}`}
              className={`rounded-xl border p-3 transition ${active ? "border-[#ccff00]/50 bg-[#ccff00]/[0.05]" : "border-white/8 bg-white/[0.02] hover:border-white/20"}`}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="truncate font-tac text-[8px] tracking-widest text-white/35">{m.league}</span>
                <span className="rounded px-1.5 font-mono text-[10px] text-[#ccff00]">{m.minute}'</span>
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <span className="truncate text-white/85">{m.home?.name}</span>
                <span className="ml-2 font-head font-black text-white">{m.home?.goals}</span>
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <span className="truncate text-white/60">{m.away?.name}</span>
                <span className="ml-2 font-head font-black text-white/70">{m.away?.goals}</span>
              </div>
              <button onClick={() => onWatch(m)} data-testid={`board-watch-${m.fixture_id}`}
                className="mt-2 w-full rounded-lg border border-white/12 py-1.5 font-tac text-[9px] tracking-widest text-white/60 transition hover:border-[#ccff00]/50 hover:text-[#ccff00]">
                {active ? "★ WATCHING" : "WATCH"}
              </button>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const selSm = "rounded-lg border border-white/10 bg-[#0a0a0a] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/50 max-w-[250px]";

// Live Sub Simulator — drop a bench player on and see the projected win-swing
const SubSimulator = ({ tiring, impact, off, on, setOff, setOn, onRun, loading, result }) => (
  <Card icon={ArrowLeftRight} title="LIVE SUB SIMULATOR" testid="sub-sim" accent="#33d17a">
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <span className="font-tac text-[9px] tracking-widest text-white/40">BRING OFF (on pitch)</span>
        <select data-testid="sub-off" value={off} onChange={(e) => setOff(e.target.value)} className={selSm}>
          <option value="">Select…</option>
          {tiring.map((p) => <option key={p.id} value={p.id}>{`${p.position} · ${p.name} (${p.effective}%)`}</option>)}
        </select>
      </div>
      <ArrowLeftRight size={16} className="mb-2.5 text-white/30" />
      <div className="flex flex-col gap-1">
        <span className="font-tac text-[9px] tracking-widest text-white/40">BRING ON (bench)</span>
        <select data-testid="sub-on" value={on} onChange={(e) => setOn(e.target.value)} className={selSm}>
          <option value="">Select…</option>
          {impact.map((p) => <option key={p.id} value={p.id}>{`${p.position} · ${p.name} (${p.readiness}%)`}</option>)}
        </select>
      </div>
      <button onClick={onRun} disabled={loading} data-testid="sub-sim-run"
        className="inline-flex items-center gap-2 rounded-full bg-[#33d17a] px-5 py-2.5 text-sm font-bold text-black transition hover:shadow-[0_0_30px_rgba(51,209,122,0.5)] disabled:opacity-50">
        {loading ? <Loader2 className="animate-spin" size={15} /> : <Zap size={15} />} Simulate swap
      </button>
    </div>
    {result && (
      <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="sub-sim-result" data-octon-section="sub simulator substitution swing" data-octon-label="Sub Simulator">
        <div className="lg:col-span-2 rounded-xl border border-white/8 bg-black/30 p-4">
          <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">WIN PROBABILITY · BEFORE → AFTER</p>
          <div className="flex items-center gap-3">
            <span className="font-head text-3xl font-black text-white/60">{result.before.win}%</span>
            <span className="text-white/30">→</span>
            <span className="font-head text-4xl font-black" style={{ color: result.win_swing >= 0 ? "#33d17a" : "#ff3b30" }} data-testid="sub-sim-after">{result.after.win}%</span>
            <span className="ml-2 rounded-full px-3 py-1 font-mono text-sm font-bold" data-testid="sub-sim-swing"
              style={{ background: result.win_swing >= 0 ? "#33d17a22" : "#ff3b3022", color: result.win_swing >= 0 ? "#33d17a" : "#ff3b30" }}>
              {result.win_swing > 0 ? "+" : ""}{result.win_swing} pts
            </span>
          </div>
          <div className="mt-2 flex gap-4 text-[11px] text-white/45">
            <span>Draw {result.before.draw}% → {result.after.draw}%</span>
            <span>Loss {result.before.loss}% → {result.after.loss}%</span>
          </div>
        </div>
        <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
          <p className="mb-1.5 font-tac text-[9px] tracking-widest text-white/40">TACTICAL EFFECT</p>
          <div className="flex justify-between text-[12px]"><span className="text-white/55">Our attack</span><span className="font-mono" style={{ color: result.effect.our_attack_pct >= 0 ? "#33d17a" : "#ff3b30" }}>{result.effect.our_attack_pct > 0 ? "+" : ""}{result.effect.our_attack_pct}%</span></div>
          <div className="flex justify-between text-[12px]"><span className="text-white/55">Opponent threat</span><span className="font-mono" style={{ color: result.effect.opp_threat_pct <= 0 ? "#33d17a" : "#ff3b30" }}>{result.effect.opp_threat_pct > 0 ? "+" : ""}{result.effect.opp_threat_pct}%</span></div>
          <p className="mt-2 text-[12px] font-semibold" style={{ color: result.win_swing >= 0.5 ? "#33d17a" : result.win_swing <= -0.5 ? "#ff3b30" : "#ffb800" }}>{result.verdict}</p>
          <p className="mt-1 text-[10px] text-white/35">{result.sub.on.name} on for {result.sub.off.name} · {result.minute}'</p>
        </div>
      </div>
    )}
  </Card>
);

export default function LiveMatch() {
  const { speak } = useAva();
  const loc = useLocation();
  const preset = loc.state || {};
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(preset.league || 39);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(preset.team_id || null);
  const [oppId, setOppId] = useState(preset.opponent_id || null);
  const [minute, setMinute] = useState(60);
  const [our, setOur] = useState(1);
  const [their, setTheir] = useState(1);
  const [ourXg, setOurXg] = useState("");
  const [theirXg, setTheirXg] = useState("");
  const [weather, setWeather] = useState("dry");
  const [venue, setVenue] = useState("home");
  const { fixture: nextFixture, markTouched } = useNextFixture(teamId, oppId, setVenue);
  const [data, setData] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [bpLoading, setBpLoading] = useState(false);
  const [stream, setStream] = useState(false);
  const [streamData, setStreamData] = useState(null);
  const [autoCall, setAutoCall] = useState(true);
  const [subOff, setSubOff] = useState("");
  const [subOn, setSubOn] = useState("");
  const [subResult, setSubResult] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [strOff, setStrOff] = useState("");
  const [strOn, setStrOn] = useState("");
  const [strResult, setStrResult] = useState(null);
  const [strLoading, setStrLoading] = useState(false);
  const [board, setBoard] = useState(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const streamTimer = useRef(null);
  const boardTimer = useRef(null);
  const pendingWatch = useRef(null);
  const lastAutoFire = useRef(-99);
  const autoCallRef = useRef(true);
  const pdfRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(false);

  const paramsRef = useRef({});
  const loadingRef = useRef(false);
  const clockTimer = useRef(null);
  const lastRunMin = useRef(-1);

  useEffect(() => { paramsRef.current = { teamId, oppId, minute, our, their, ourXg, theirXg, weather, venue }; }, [teamId, oppId, minute, our, their, ourXg, theirXg, weather, venue]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { autoCallRef.current = autoCall; }, [autoCall]);
  useEffect(() => () => clearInterval(clockTimer.current), []);

  useEffect(() => { api.get("/leagues").then((r) => setLeagues(r.data)); }, []);
  useEffect(() => {
    let live = true;
    api.get(`/teams?league=${league}`).then((r) => {
      if (!live) return;
      setTeams(r.data);
      const pw = pendingWatch.current;
      if (pw) { setTeamId(pw.teamId); setOppId(pw.oppId); pendingWatch.current = null; }
      else if (preset.pendingFixture) { /* voice fixture on its way — hold */ }
      else if (preset.team_id || preset.opponent_id) {
        setTeamId((v) => v || preset.team_id || r.data[0]?.id);
        setOppId((v) => v || preset.opponent_id || r.data[1]?.id);
      }
      else {
        setTeamId((v) => (v && r.data.some((t) => t.id === Number(v)) ? v : r.data[0]?.id));
        setOppId((v) => (v && r.data.some((t) => t.id === Number(v)) ? v : r.data[1]?.id));
      }
    });
    return () => { live = false; };
    // eslint-disable-next-line
  }, [league]);

  const runLive = (ov = {}) => {
    const p = { ...paramsRef.current, ...ov };
    if (!p.teamId || !p.oppId || p.teamId === p.oppId) { toast.error("Pick two different clubs"); return; }
    setLoading(true);
    lastRunMin.current = p.minute;
    const payload = { team_id: Number(p.teamId), opponent_id: Number(p.oppId), minute: p.minute, our_score: p.our, their_score: p.their, weather: p.weather, venue: p.venue };
    if (p.ourXg !== "" && p.ourXg != null) payload.our_xg = Number(p.ourXg);
    if (p.theirXg !== "" && p.theirXg != null) payload.their_xg = Number(p.theirXg);
    api.post("/matchday/live", payload)
      .then((r) => { setData(r.data); const l = r.data.live || {}; speak(`${l.priority || ""} ${l.one_liner || ""}`); })
      .catch(() => toast.error("OCTON couldn't compute the live call"))
      .finally(() => setLoading(false));
  };
  const run = () => { setData(null); runLive(); };
  useVoiceFixture({ setLeague, setTeamId, setOppId, onPending: () => setData(null), onFixture: () => setData(null), onRerun: run });

  const runBlueprint = () => {
    if (!teamId || !oppId || teamId === oppId) { toast.error("Pick two different clubs"); return; }
    setBpLoading(true);
    api.get(`/tactical/matrix?team_id=${teamId}&opponent_id=${oppId}&venue=${venue}`)
      .then((r) => setBlueprint(r.data))
      .catch(() => toast.error("Couldn't compute the tactical blueprint"))
      .finally(() => setBpLoading(false));
  };

  const pollStream = () => {
    const p = paramsRef.current;
    if (!p.teamId || !p.oppId || p.teamId === p.oppId) return;
    api.get(`/tactical/live-ingest?team_id=${Number(p.teamId)}&opponent_id=${Number(p.oppId)}&venue=${p.venue}`)
      .then((r) => {
        setStreamData(r.data);
        if (r.data.live) {  // auto-ingest the live feed into the touchline inputs
          setMinute(Math.min(90, r.data.minute));
          setOur(r.data.score.us); setTheir(r.data.score.them);
          setOurXg(String(r.data.live_xg.us)); setTheirXg(String(r.data.live_xg.them));
          // Auto Touchline Call — fire OCTON's full LLM plan when the stream flags CRITICAL ACTION
          if (autoCallRef.current && r.data.engine?.underperforming && !loadingRef.current
              && r.data.minute - lastAutoFire.current >= 8) {
            lastAutoFire.current = r.data.minute;
            toast.warning(`OCTON auto-fired a touchline call — CRITICAL at ${r.data.minute}'`);
            runLive({ minute: Math.min(90, r.data.minute), our: r.data.score.us, their: r.data.score.them,
                      ourXg: String(r.data.live_xg.us), theirXg: String(r.data.live_xg.them) });
          }
        }
      })
      .catch(() => {});
  };
  const stopStream = () => { clearInterval(streamTimer.current); streamTimer.current = null; setStream(false); };
  const toggleStream = () => {
    if (stream) { stopStream(); return; }
    if (!teamId || !oppId || teamId === oppId) { toast.error("Pick two different clubs"); return; }
    setStream(true);
    pollStream();
    streamTimer.current = setInterval(pollStream, 30000);
  };
  useEffect(() => () => { clearInterval(streamTimer.current); clearInterval(boardTimer.current); }, []);

  const runSubSim = () => {
    if (!subOff || !subOn) { toast.error("Pick a player to bring off and a player to bring on"); return; }
    if (subOff === subOn) { toast.error("Pick two different players"); return; }
    const p = paramsRef.current;
    setSubLoading(true);
    const payload = { team_id: Number(p.teamId), opponent_id: Number(p.oppId), venue: p.venue,
      minute: p.minute, our_score: p.our, their_score: p.their, off_id: Number(subOff), on_id: Number(subOn) };
    if (p.ourXg !== "" && p.ourXg != null) payload.our_xg = Number(p.ourXg);
    if (p.theirXg !== "" && p.theirXg != null) payload.their_xg = Number(p.theirXg);
    api.post("/tactical/sub-sim", payload)
      .then((r) => setSubResult(r.data))
      .catch(() => toast.error("Couldn't simulate that substitution"))
      .finally(() => setSubLoading(false));
  };

  const runStreamSub = () => {
    if (!strOff || !strOn) { toast.error("Pick a player to bring off and a player to bring on"); return; }
    if (strOff === strOn) { toast.error("Pick two different players"); return; }
    const p = paramsRef.current;
    setStrLoading(true);
    const payload = { team_id: Number(p.teamId), opponent_id: Number(p.oppId), venue: p.venue,
      minute: p.minute, our_score: p.our, their_score: p.their, off_id: Number(strOff), on_id: Number(strOn) };
    if (p.ourXg !== "" && p.ourXg != null) payload.our_xg = Number(p.ourXg);
    if (p.theirXg !== "" && p.theirXg != null) payload.their_xg = Number(p.theirXg);
    api.post("/tactical/sub-sim", payload)
      .then((r) => setStrResult(r.data))
      .catch(() => toast.error("Couldn't trial that substitution"))
      .finally(() => setStrLoading(false));
  };

  const pollBoard = () => {
    api.get("/tactical/live-board?limit=40").then((r) => setBoard(r.data)).catch(() => {});
  };
  const toggleBoard = () => {
    if (boardOpen) { clearInterval(boardTimer.current); boardTimer.current = null; setBoardOpen(false); return; }
    setBoardOpen(true);
    pollBoard();
    boardTimer.current = setInterval(pollBoard, 30000);
  };
  const watchMatch = (m) => {
    if (!m.home?.id || !m.away?.id) { toast.error("This match has incomplete team data"); return; }
    pendingWatch.current = { teamId: m.home.id, oppId: m.away.id };
    setStreamData(null); setSubResult(null);
    if (m.league_id && m.league_id !== league) { setLeague(m.league_id); }
    else { setTeamId(m.home.id); setOppId(m.away.id); }
    setVenue("home");
    toast.success(`Watching ${m.home.name} vs ${m.away.name} — hit GO LIVE to stream`);
  };

  const stopClock = () => { clearInterval(clockTimer.current); clockTimer.current = null; setClock(false); };
  const toggleClock = () => {
    if (clock) { stopClock(); return; }
    if (!teamId || !oppId || teamId === oppId) { toast.error("Pick two different clubs"); return; }
    setClock(true);
    runLive();                                   // immediate call at current minute
    clockTimer.current = setInterval(() => {
      setMinute((prev) => {
        const nm = Math.min(90, prev + 1);
        if (nm >= 90) stopClock();
        // auto re-run every 5 match-minutes (if not already computing)
        if (nm % 5 === 0 && nm !== lastRunMin.current && !loadingRef.current) runLive({ minute: nm });
        return nm;
      });
    }, 4000);                                    // 1 match-minute ≈ 4s real time
  };
  // when a goal goes in during the live clock, re-read the game immediately
  const setScore = (side, val) => {
    const v = Math.max(0, val);
    if (side === "our") setOur(v); else setTheir(v);
    if (clock && !loadingRef.current) runLive(side === "our" ? { our: v } : { their: v });
  };

  const l = data?.live;
  const gs = data?.game_state;

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16" data-testid="live-match-page" data-octon-fixture={teamId && oppId ? `${Number(teamId)}-${Number(oppId)}` : ""} data-octon-fixture-label={teamId && oppId ? `${teams.find((t) => t.id === Number(teamId))?.name || ""} vs ${teams.find((t) => t.id === Number(oppId))?.name || ""}` : ""}>
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">LIVE MATCH · TOUCHLINE ASSISTANT</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Live Match Mode</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/50">Feed OCTON the live score and clock — it re-times your substitutions and shape to <span className="text-white">this exact game state and fatigue picture</span>, in-game.</p>

      {/* controls */}
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">LEAGUE</label>
          <LeagueSelect testId="live-league" value={league} onChange={(id) => setLeague(id)} /></div>
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">YOUR CLUB</label>
          <TeamSelect testId="live-team" teams={teams} value={teamId} onChange={(id) => setTeamId(id)} /></div>
        <Swords className="mb-3 text-white/30" size={18} />
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">OPPONENT</label>
          <TeamSelect testId="live-opp" teams={teams} value={oppId} onChange={(id) => setOppId(id)} placeholder="Opponent" /></div>
      </div>

      {teamId && oppId && <FixtureBanner fixture={nextFixture} teamName={teams.find((t) => t.id === Number(teamId))?.name} />}
      {/* live game state */}
      <div className="mt-5 flex flex-wrap items-center gap-6 rounded-2xl border border-white/10 bg-black/30 p-5"
        data-octon-section="live game state score minute clock xg venue weather" data-octon-label="Live Game State">
        <Stepper label="OUR GOALS" value={our} set={(v) => setScore("our", v)} testid="live-our" />
        <span className="font-head text-2xl font-black text-white/30">—</span>
        <Stepper label="THEIR GOALS" value={their} set={(v) => setScore("their", v)} testid="live-their" />
        <div className="flex flex-col items-center gap-1">
          <span className="font-tac text-[9px] tracking-widest text-white/40">LIVE xG (OUR)</span>
          <input type="number" step="0.05" min="0" value={ourXg} onChange={(e) => setOurXg(e.target.value)} placeholder="auto"
            data-testid="live-our-xg" className="w-20 rounded-lg border border-white/10 bg-[#0a0a0a] px-2 py-1.5 text-center font-mono text-sm text-white outline-none focus:border-cyan-400/50" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="font-tac text-[9px] tracking-widest text-white/40">LIVE xG (THEIR)</span>
          <input type="number" step="0.05" min="0" value={theirXg} onChange={(e) => setTheirXg(e.target.value)} placeholder="auto"
            data-testid="live-their-xg" className="w-20 rounded-lg border border-white/10 bg-[#0a0a0a] px-2 py-1.5 text-center font-mono text-sm text-white outline-none focus:border-cyan-400/50" />
        </div>
        <div className="flex flex-1 flex-col gap-1" style={{ minWidth: 220 }}>
          <label className="flex items-center gap-2 font-tac text-[9px] tracking-widest text-white/40">MINUTE · <span className="txt-cyan">{minute}'</span>
            {clock && <span className="inline-flex items-center gap-1 text-[#ccff00]" data-testid="live-clock-indicator"><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#ccff00] opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#ccff00]" /></span>LIVE · auto-advancing</span>}
          </label>
          <input type="range" min="1" max="90" value={minute} onChange={(e) => setMinute(Number(e.target.value))} data-testid="live-minute" className="w-full accent-cyan-400" />
        </div>
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">VENUE</label>
          <select data-testid="live-venue" value={venue} onChange={(e) => { markTouched(); setVenue(e.target.value); }} className={sel}><option value="home">Home</option><option value="away">Away</option><option value="neutral">Neutral</option></select></div>
        <div className="flex flex-col gap-1"><label className="font-tac text-[9px] tracking-widest text-white/40">WEATHER <span className="text-amber-300/60">· your selection</span></label>
          <div className="flex gap-1.5">{WEATHER.map((w) => { const I = w.i; return (
            <button key={w.v} onClick={() => setWeather(w.v)} data-testid={`live-weather-${w.v}`} title={w.l}
              className={`rounded-lg border px-2.5 py-2 transition ${weather === w.v ? "border-cyan-400 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-white/40 hover:text-white"}`}><I size={15} /></button>); })}</div></div>
        <div className="ml-auto mb-0.5 flex items-center gap-2">
          <button onClick={toggleClock} data-testid="live-clock-toggle"
            className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-bold transition ${clock ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]" : "border-white/15 text-white/70 hover:text-white"}`}>
            {clock ? <><Pause size={15} /> Clock running</> : <><Play size={15} /> Kick off clock</>}
          </button>
          <button onClick={run} disabled={loading} data-testid="live-run" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition hover:shadow-[0_0_40px_rgba(0,229,255,0.5)] disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />} Get live call</button>
        </div>
      </div>

      {/* Pre-match tactical blueprint (coaching-matrix formation optimizer) */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={runBlueprint} disabled={bpLoading} data-testid="blueprint-run"
          className="inline-flex items-center gap-2 rounded-full border border-[#ccff00]/40 bg-[#ccff00]/10 px-5 py-2.5 text-sm font-bold text-[#ccff00] transition hover:bg-[#ccff00]/20 disabled:opacity-50">
          {bpLoading ? <Loader2 className="animate-spin" size={15} /> : <Grid3x3 size={15} />} Run tactical blueprint
        </button>
        <button onClick={toggleStream} data-testid="stream-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition ${stream ? "border-[#ccff00] bg-[#ccff00]/15 text-[#ccff00]" : "border-white/15 text-white/70 hover:text-white"}`}>
          <Radio size={15} className={stream ? "animate-pulse" : ""} /> {stream ? "Streaming live data…" : "GO LIVE — connect stream"}
        </button>
        <button onClick={toggleBoard} data-testid="board-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition ${boardOpen ? "border-cyan-400 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-white/70 hover:text-white"}`}>
          <Globe size={15} /> {boardOpen ? "Hide live board" : "Live board"}
        </button>
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-white/55" data-testid="auto-call-toggle">
          <input type="checkbox" checked={autoCall} onChange={(e) => setAutoCall(e.target.checked)} className="accent-[#ccff00]" />
          Auto touchline call on CRITICAL
        </label>
        <span className="text-[11px] text-white/40">Optimise before kick-off, then auto-ingest the real live feed (API-Football + web cross-check).</span>
      </div>
      {boardOpen && <div className="mt-4"><LiveBoard board={board} onWatch={watchMatch} activeIds={new Set([Number(teamId), Number(oppId)])} /></div>}
      {blueprint && <div className="mt-4"><Blueprint bp={blueprint} /></div>}
      {streamData && <div className="mt-4"><StreamPanel s={streamData} off={strOff} on={strOn}
        setOff={setStrOff} setOn={setStrOn} onTrial={runStreamSub} trialLoading={strLoading} trialResult={strResult} /></div>}

      {loading && <div className="mt-4 card-tac flex items-center gap-3 p-6 text-sm text-white/60"><Loader2 className="animate-spin txt-cyan" size={18} /> OCTON is reading the live game state, fatigue and momentum…</div>}

      {l && gs && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-4" data-testid="live-result" data-octon-section="live touchline call substitutions shape adjustment" data-octon-label="Touchline Call" ref={pdfRef}>
          <div className="flex justify-end gap-2"><DownloadPdfButton targetRef={pdfRef} title="Live Match Report" subtitle={`${data.team} ${gs.our_score}-${gs.their_score} ${data.opponent} · ${gs.minute}'`} filename="octon-live-match.pdf" testid="live-pdf-btn" /><EmailReportButton targetRef={pdfRef} kind="live" title="Live Match Report" subtitle={`${data.team} ${gs.our_score}-${gs.their_score} ${data.opponent} · ${gs.minute}'`} testid="live-email-btn" /></div>
          {/* scoreboard */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-400/[0.07] to-transparent p-6">
            <div className="flex items-center gap-4">
              <span className="truncate font-head text-2xl font-black">{data.team}</span>
              <span className="font-head text-5xl font-black txt-cyan">{gs.our_score}<span className="mx-2 text-white/30">-</span>{gs.their_score}</span>
              <span className="truncate font-head text-2xl font-black text-white/60">{data.opponent}</span>
            </div>
            <div className="text-right">
              <p className="font-head text-3xl font-black" style={{ color: "#ccff00" }}>{gs.minute}'</p>
              <p className="font-tac text-[9px] tracking-widest text-white/40">{gs.state.toUpperCase()} · {gs.remaining}' LEFT · {data.shape}</p>
            </div>
          </div>

          {data.momentum && <MomentumBar mom={data.momentum} team={data.team} opponent={data.opponent} />}

          {data.on_pitch?.length > 0 && (
            <div className="card-tac p-5" data-testid="live-on-pitch" data-octon-section="on the pitch now team sheet substitutions made who came on off bench left" data-octon-label="On The Pitch" data-octon-state="ready">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-tac text-[9px] tracking-widest txt-cyan">ON THE PITCH · {data.on_pitch.length}</p>
                <p className="font-tac text-[8px] tracking-widest text-white/35" data-testid="live-on-pitch-source">{(data.on_pitch_source || "").toUpperCase()}</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.on_pitch.map((p) => (
                  <span key={p.id} className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[11px] text-white/75">{p.name} <span className="text-white/30">· {p.position}</span></span>
                ))}
              </div>
              {data.live_changes?.length > 0 ? (
                <div className="mt-3" data-testid="live-changes">
                  <p className="font-tac text-[8px] tracking-widest text-white/35">SUBSTITUTIONS MADE · {data.subs_used} OF 5 · {data.subs_left} LEFT</p>
                  <ul className="mt-1 space-y-0.5 text-[12px] text-white/70">
                    {data.live_changes.map((c, i) => <li key={i}><span className="font-mono text-white/40">{c.minute}'</span> {c.off} <span className="text-red-300/70">off</span> → {c.on} <span className="text-emerald-300/80">on</span></li>)}
                  </ul>
                </div>
              ) : data.subs_used === 0 ? <p className="mt-2 text-[11px] text-white/40">No substitutions made yet — 5 changes available.</p> : null}
              {data.bench_left?.length > 0 && <p className="mt-2 text-[11px] text-white/40">Bench left: {data.bench_left.map((b) => b.name).join(", ")}</p>}
            </div>
          )}

          {data.coaching_matrix && <CoachingMatrix cm={data.coaching_matrix} />}

          {(data.tiring?.length > 0 && data.impact?.length > 0) && (
            <SubSimulator tiring={data.tiring} impact={data.impact} off={subOff} on={subOn}
              setOff={setSubOff} setOn={setSubOn} onRun={runSubSim} loading={subLoading} result={subResult} />
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <Card icon={Activity} title="OCTON READS THE GAME" testid="live-read"><p className="text-[13px] leading-relaxed text-white/75">{l.game_read}</p>
              {l.priority && <p className="mt-3 rounded-lg border-l-2 border-[#ccff00] bg-black/30 px-3 py-2 text-[13px] text-white/85"><span className="text-white/40">Priority now: </span>{l.priority}</p>}
            </Card>
            <Card icon={Flame} title="TIRING ON THE PITCH" testid="live-tiring" accent="#ff7300">
              <div className="space-y-2">{(data.tiring || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between text-[12px]"><span className="text-white/70"><span className="mr-1.5 font-mono text-[9px] text-white/35">{t.position}</span>{t.name}</span>
                  <span className="font-mono" style={{ color: eColor(t.effective) }}>{t.effective}%</span></div>))}</div>
              <p className="mt-2 text-[10px] text-white/40">Effective readiness at minute {gs.minute} — starting readiness decayed by minutes-in-fatigue.</p>
            </Card>
            <Card icon={ShieldAlert} title="RISK · NEXT 15'" testid="live-risk" accent="#ff3b30"><p className="text-[13px] leading-relaxed text-white/75">{l.risk}</p></Card>
          </div>

          <Card icon={ArrowLeftRight} title="RE-TIMED SUBSTITUTIONS" testid="live-subs" accent="#ffb800">
            <div className="space-y-3">{(l.next_subs || []).map((s, i) => (
              <div key={i} className="rounded-xl border border-white/8 bg-white/[0.02] p-3.5" data-testid={`live-sub-${i}`}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="rounded-md px-2 py-0.5 font-mono text-[11px]" style={{ background: "#ffb80022", color: "#ffb800" }}>{s.window}</span>
                  <span className="font-tac text-[8px] tracking-widest text-white/35">SUB {i + 1}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  <span className="text-[#ff7300]">↓ {s.off}</span>
                  <ArrowLeftRight size={13} className="text-white/30" />
                  <span className="text-[#33d17a]">↑ {s.on}</span>
                </div>
                {s.why && <p className="mt-1.5 text-[12px] leading-snug text-white/60">{s.why}</p>}
              </div>))}</div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card icon={Zap} title="TACTICAL ADJUSTMENT" testid="live-adjustment"><p className="text-[13px] leading-relaxed text-white/75">{l.adjustment}</p></Card>
            <div className="flex flex-col justify-between rounded-2xl border border-[#ccff00]/30 bg-[#ccff00]/[0.04] p-5" data-testid="live-oneliner">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-tac text-[9px] tracking-widest" style={{ color: "#ccff00" }}>DECISIVE CALL</span>
                  <button onClick={() => speak(`${l.priority || ""} ${l.one_liner || ""}`, { force: true })} data-testid="live-speak" className="text-cyan-300 hover:text-white"><Volume2 size={15} /></button>
                </div>
                <p className="text-[15px] font-semibold italic leading-relaxed text-white/90">"{l.one_liner}"</p>
              </div>
            </div>
          </div>
          <p className="pt-1 text-center text-[11px] text-white/30">OCTON super-brain · live fatigue model · AVA fleet</p>
        </motion.div>
      )}
    </div>
  );
}
